import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import BaseChart from "../../components/ui/BaseChart";
import { fetchProfitTrend } from "../../services/api/invoiceAnalytics";

const CURRENT_YEAR = new Date().getFullYear();

const compactUsd = (n) => {
  const v = Number(n ?? 0);
  if (Math.abs(v) >= 1000) return `${v < 0 ? "-" : ""}$${(Math.abs(v) / 1000).toFixed(1)}k`;
  return `${v < 0 ? "-" : ""}$${Math.abs(v).toFixed(0)}`;
};

const fullUsd = (n) =>
  `$${Number(n ?? 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const ProfitTrendChart = ({ clientId }) => {
  const { data: result, isLoading } = useQuery({
    queryKey: ["profit-trend", CURRENT_YEAR, clientId],
    queryFn: () =>
      fetchProfitTrend({ year: CURRENT_YEAR, ...(clientId ? { clientId } : {}) }),
    staleTime: 60_000,
  });

  const trend = result?.data?.data;

  const chartOptions = useMemo(() => {
    if (!trend?.length) return null;
    const categories = trend.map((d) => d.month);
    const revenue = trend.map((d) => d.revenue);
    const expense = trend.map((d) => d.expense);
    const profit = trend.map((d) => d.profit);

    return {
      chart: {
        height: 380,
        spacing: [12, 12, 8, 8],
        backgroundColor: "transparent",
        style: { fontFamily: "Poppins, sans-serif" },
      },
      title: { text: null },
      xAxis: {
        categories,
        lineColor: "#e5e7eb",
        tickLength: 0,
        labels: { style: { fontSize: "10px", color: "#9ca3af" } },
        crosshair: { color: "#f1f5f9" },
      },
      yAxis: {
        title: { text: null },
        gridLineColor: "#f3f4f6",
        plotLines: [{ value: 0, color: "#cbd5e1", width: 1, zIndex: 3 }],
        labels: {
          formatter() {
            return compactUsd(this.value);
          },
          style: { fontSize: "10px", color: "#9ca3af" },
        },
      },
      legend: {
        align: "center",
        verticalAlign: "bottom",
        layout: "horizontal",
        margin: 24,
        itemStyle: { fontSize: "10px", fontWeight: "normal", color: "#555" },
        symbolRadius: 3,
        symbolHeight: 8,
      },
      tooltip: {
        shared: true,
        backgroundColor: "#1e293b",
        style: { color: "#f8fafc" },
        borderWidth: 0,
        borderRadius: 6,
        useHTML: true,
        formatter() {
          const pts = this.points ?? [];
          const lines = pts.map(
            (p) =>
              `<span style="color:${p.color}">●</span> ${p.series.name}: <b>${fullUsd(p.y)}</b>`,
          );
          return `<b>${this.x} ${CURRENT_YEAR}</b><br/>${lines.join("<br/>")}`;
        },
      },
      plotOptions: {
        column: { borderRadius: 4, borderWidth: 0, groupPadding: 0.12 },
        spline: { marker: { radius: 3, symbol: "circle" }, lineWidth: 2.5 },
      },
      series: [
        { type: "column", name: "Revenue", data: revenue, color: "#86EFAC", yAxis: 0 },
        { type: "column", name: "Expense", data: expense, color: "#FCA5A5", yAxis: 0 },
        {
          type: "spline",
          name: "Profit",
          data: profit,
          color: "#0D9488",
          yAxis: 0,
          marker: { fillColor: "#0D9488" },
          zIndex: 5,
        },
      ],
    };
  }, [trend]);

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <h4 className="text-sm font-semibold text-gray-700">Profit Trend — {CURRENT_YEAR}</h4>
          <p className="text-[11px] text-gray-400 mt-0.5">
            Monthly revenue, expense &amp; profit · USD
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-[380px]">
          <div className="w-7 h-7 border-[3px] border-teal-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : chartOptions ? (
        <BaseChart options={chartOptions} className="w-full" />
      ) : (
        <p className="text-sm text-gray-400 py-20 text-center">No profit data for {CURRENT_YEAR}.</p>
      )}
    </div>
  );
};

export default ProfitTrendChart;
